/**
 * @repo/supabase - Shared Supabase utilities
 * 
 * This package provides:
 * - Client creation (browser and server)
 * - Service layer for common operations
 * - Shared types
 * - Error handling utilities
 */

// Client exports (browser only - server client should be imported from @repo/supabase/server)
export { createBrowserClient, supabase, createBrowserCookieClient, supabaseCookieClient } from './client'

// Service exports
export {
  authService,
  getEnrollmentProgress,
  // Messaging: intentionally NOT re-exported here (uses
  // `import 'server-only'`). Import via `@repo/supabase/messaging`.
  portalAuthService,
  profileService,
  transformToPublicTherapist,
  userService,
  healthRecordService,
  episodeService,
  storageService,
  auditService,
  onboardingService,
  errorLogger,
  getErrorLogger,
  createAvailabilityService,
  meetingService,
  createMeetingService,
  createEventsService,
  canHostEvents,
  canHostEvent,
  canEditEvent,
  canSeeEvent,
  createErrorTrackingService,
  saveQuizData,
  saveTempQuizData,
  completeTempQuiz,
  findMatchingPros,
  updateMatchingQuizWithPros,
  runMatching,
  insertBesReferralsForAlliMatch,
  getProAvailability,
  createInitialSession,
  getTherapistSessions,
  getSessionAttendees,
  updateSessionStatus,
  getSessionsByEpisode,
  getSessionsByGroup,
  validateBooking,
  createBooking,
  createProgressNote,
  updateProgressNote,
  getProgressNote,
  getProgressNoteBySessionId,
  getProgressNotesByEpisode,
  getProgressNotesByGroup,
  deleteProgressNote,
  createAssessment,
  updateAssessment,
  getAssessment,
  getAssessmentBySessionId,
  getAssessmentsByEpisode,
  getAssessmentsByGroup,
  deleteAssessment,
  getIntakeFormsByUserId,
  getLatestIntakeFormByUserId,
  getIntakeFormsByHealthRecordId,
  getIntakeFormsByEpisode,
  getIntakeFormById,
  consentService,
  therapyGroupService,
  developmentIssuesService,
  supportService,
  checkAnonRateLimit,
  createSupportTicket,
  appendSupportMessage,
  getSupportTicketByToken,
  stripInternalMessages,
  promoCodesService,
  pickProEmail,
  isAlliInternal,
  PRO_EMAIL_SELECT,
  refundAttendeeCycle,
  validatePromoCode,
  calculateDiscountedRate,
  getPromoCode,
  getPromoDisplayText,
  createAdminEmailService,
  createEmployerWallet,
  createPromoWallet,
  addAuthorizedUser,
  removeAuthorizedUser,
  findEmployeeWallet,
  findBestAvailableWallet,
  reserveWalletSession,
  updateWalletTransactionStatus,
  calculateWalletBalance,
  getTotalAvailableSessions,
  invalidateWallet,
  mapWalletSourceToPaymentSource,
  careService,
  createCareRequest,
  supervisorApproveCare,
  supervisorRejectCare,
  cancelActiveCare,
  expireCareRequest,
  listCareForTherapist,
  listCareForSupervisor,
  countActiveCareForTherapist,
  countPendingCareForSupervisor,
  getCareRequest,
  signedCareProofUrl,
  listCareRequestsToExpire,
  listCareRequestsForReminder,
  CareError,
  CARE_RATE_BANDS,
  CARE_MAX_DURATION_MONTHS,
  CARE_MAX_ACTIVE_PER_THERAPIST,
  CARE_HARDSHIP_PROOF_BUCKET
} from './services'

// NOTE: sessions helpers (`fetchUserSessions`, `cancelUserSession`) live at
// `@repo/supabase/sessions`. They import `@repo/email` (SendGrid), so leaving
// them in the top-level barrel poisons every client bundle with `fs`.

export type {
  CareTier,
  CareRequestStatus,
  CareProofDocument,
  CareRequest,
  CreateCareRequestInput,
} from './services/care'
export type { RefundAttendeeResult } from './services/supervision-refund'

// Type exports
export * from './types'
export type { AvailabilityData, CreateAvailabilityData } from './services/availability'
export type { CreateMeetingData } from './services/meetings'
export type {
  EventType,
  EventAudienceScope,
  EventEnrollmentMode,
  EventStatus,
  EventAttendeeStatus,
  EventAttendeeSource,
  PdActivityCategory,
  EventAudienceFilter,
  EventRow,
  EventAttendeeRow,
  EventSeriesRow,
  EventSeriesPattern,
  EventSeriesMonthlyMode,
  EventSeriesStatus,
  EventRecurrenceSpec,
  AuthzPro,
  CreateEventInput,
  UpdateEventInput,
  EventsService,
} from './services/events'
export type { ErrorLogData } from './services/error-tracking'
export type { QuizData } from './services/quiz'
export type { TimeSlot, AvailabilityResponse, CreateInitialSessionParams, Session } from './services/session'
export type { UserProfileData } from './services/user'
export type { HealthRecordData } from './services/health-record'
export type { EpisodeData, CreateEpisodeParams } from './services/episode'
export type { IntakeFormData } from './services/intake'
export type { ProgressNote, ProgressNoteData } from './services/progress-notes'
export type { ConsentData, CreateConsentInput } from './services/consent'
export type { PromoCode } from './services/promo'
export type { TherapyGroupData, TherapyGroupWithEpisodes } from './services/therapy-group'
export type { DevelopmentIssue, CreateIssueInput, UpdateIssueInput } from './services/development-issues'
export type {
  SupportTicketApp,
  SupportTicketCategory,
  SupportTicketStatus,
  SupportTicketPriority,
  SupportMessageRole,
  SupportMessageVisibility,
  SupportAttachment,
  SupportMessage,
  CreateSupportTicketInput,
  CreatedSupportTicket,
} from './services/support'
export type { PromoCode as PromoCodeData, CreatePromoCodeInput, UpdatePromoCodeInput } from './services/promo-codes'
export type { MatchingCriteria } from './services/matching'
export type { ChangeEmailParams, ChangeEmailResult } from './services/admin-email'
export type { SessionWallet, CreateEmployerWalletParams, WalletBalance, WalletTransactionStatus } from './services/wallet'
export type { ProEmailFields } from './services/pro-email'

// Utility exports
export { parseSupabaseAuthError, logAuthError, isAuthRejection } from './utils/auth-errors'
export type { AuthError } from './utils/auth-errors'
export * from './utils/formatting'
export { getClientIp } from './utils/ip'
export {
  REFLECTION_MIN_MINUTES,
  effectiveReflectionDurationMinutes,
  meetsReflectionDurationMinimum,
} from './utils/reflection-eligibility'
export type { ReflectionDurationInputs } from './utils/reflection-eligibility'
export { safeUrlForLog } from './utils/safe-url'

// Portal auth types
export type { PortalType, PortalAuthResult } from './services/portal-auth'